/**
 * TemplateWelcomeView Component
 *
 * Welcome/empty state view when no template is selected.
 * Shows introduction text and call-to-action to browse library
 * or upload a template.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CloudUpload as UploadIcon,
  Draw as DrawIcon,
  LibraryBooks as LibraryIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { EDITOR_MAX_WIDTH_WITH_PADDING } from '../constants/layout';

interface TemplateWelcomeViewProps {
  /** Callback to browse library */
  onBrowseLibrary: () => void;
}

export const TemplateWelcomeView: React.FC<TemplateWelcomeViewProps> = ({
  onBrowseLibrary,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: EDITOR_MAX_WIDTH_WITH_PADDING,
        mx: 'auto',
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Hero Card */}
      <Card
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          mb: 4,
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 3,
            }}
          >
            <DrawIcon
              sx={{
                fontSize: 32,
                color: 'primary.main',
              }}
            />
            <Typography variant="h4" fontWeight={700} color="text.primary">
              Template Builder
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 4,
            }}
          >
            {/* Text Content */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                fontWeight={600}
                color="primary.main"
                sx={{ mb: 2 }}
              >
                Bring your own template to JotPsych!
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, lineHeight: 1.7 }}
              >
                Have a template or note document you already use? Upload it to
                JotPsych and we&apos;ll write your encounter notes just the way
                you like.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<UploadIcon />}
                onClick={() => navigate('/templates/build')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                }}
              >
                Start with Your Template
              </Button>
            </Box>

            {/* Hero Image */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src="onboarding-images/template_builder_hero.png"
                alt="Template Builder"
                sx={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: theme.shadows[4],
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Info Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
          Welcome to the template builder!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          JotPsych can organize your note contents based on your preferences.
          Use the template builder to add, remove, or re-order sections within a
          template, or create new templates. Start with a pre-built template
          from our library or create your own custom template from scratch.
        </Typography>
      </Box>

      {/* Quick Actions */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        <Card
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            cursor: 'pointer',
            transition: theme.transitions.create(['border-color', 'box-shadow'], {
              duration: theme.transitions.duration.short,
            }),
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: theme.shadows[2],
            },
          }}
          onClick={onBrowseLibrary}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
              <LibraryIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                Browse Library
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Explore our collection of pre-built templates for various
              specialties and use cases.
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            cursor: 'pointer',
            transition: theme.transitions.create(['border-color', 'box-shadow'], {
              duration: theme.transitions.duration.short,
            }),
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: theme.shadows[2],
            },
          }}
          onClick={() => navigate('/templates/build')}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
              <UploadIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                Upload Template
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Import your existing template or note document to create a custom
              JotPsych template.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
